import * as React from "react";

import type {
  BedrockCatalogModel,
  BulkOrgModelConfigResponse,
} from "@/shared/domain/orgs";
import {
  Checkbox,
  Dialog,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";

import styles from "./AiModels.module.css";
import { offerBlocker } from "./bedrockModelForm";
import { BulkActionApply, BulkResultSummary } from "./BulkActionApply";

interface AddModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The orgs to add the model to, as targeted when the dialog opened. */
  orgIds: readonly string[];
  /** The catalog's chat models; unverified or unpriced ones are shown but not selectable. */
  chatModels: readonly BedrockCatalogModel[];
  /** Fired when the dialog closes after the change was applied. */
  onApplied: () => void;
}

/**
 * Add one catalog chat model to the targeted orgs, optionally as their
 * default: pick the model and apply, then see who was updated or skipped.
 */
export function AddModelDialog({
  open,
  onOpenChange,
  orgIds,
  chatModels,
  onApplied,
}: AddModelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={styles.dialog}>
        {open && (
          <AddModelBody
            // A fresh body per opening: new target, no earlier result.
            orgIds={orgIds}
            chatModels={chatModels}
            onClose={(applied) => {
              onOpenChange(false);
              if (applied) {
                onApplied();
              }
            }}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function AddModelBody({
  orgIds: initialOrgIds,
  chatModels,
  onClose,
}: {
  orgIds: readonly string[];
  chatModels: readonly BedrockCatalogModel[];
  onClose: (applied: boolean) => void;
}) {
  // Snapshot the target: the page may clear its selection behind the dialog.
  const [orgIds] = React.useState(() => [...initialOrgIds]);
  const [profileId, setProfileId] = React.useState<string | null>(null);
  const [makeDefault, setMakeDefault] = React.useState(false);
  const [applied, setApplied] =
    React.useState<BulkOrgModelConfigResponse | null>(null);

  if (applied !== null) {
    return (
      <div className={styles.dialogBody}>
        <Dialog.Title>Model added</Dialog.Title>
        <BulkResultSummary result={applied} verb="Updated" />
        <div className={styles.dialogActions}>
          <RobotoButton onClick={() => onClose(true)}>Done</RobotoButton>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dialogBody}>
      <Dialog.Title>
        Add a model to {orgIds.length} {orgIds.length === 1 ? "org" : "orgs"}
      </Dialog.Title>
      <p className={styles.note}>
        Each org gets the model added to its list as a Roboto-managed model;
        models on its own connections are kept.{" "}
        {makeDefault
          ? "Orgs where it is already the default are skipped."
          : "Orgs that already have it are skipped."}
      </p>
      {/* A radio list rather than a Select: the Select popup layers below
          the Dialog, and the catalog is short enough to show whole. */}
      <RadioGroup<string>
        legend="Model"
        value={profileId}
        onValueChange={setProfileId}
      >
        {chatModels.map((model) => {
          const blocker = offerBlocker(model);
          return (
            <Radio<string>
              key={model.id}
              value={model.id}
              label={
                blocker === undefined
                  ? model.label
                  : `${model.label} (${blocker.replace(/\.$/, "").toLowerCase()})`
              }
              disabled={blocker !== undefined}
            />
          );
        })}
      </RadioGroup>
      <Checkbox
        label="Make it the default"
        description="New chats in these orgs start on this model."
        checked={makeDefault}
        onCheckedChange={setMakeDefault}
      />
      <BulkActionApply
        orgIds={orgIds}
        action={
          profileId === null
            ? null
            : {
                type: "add_model",
                profile_id: profileId,
                make_default: makeDefault,
              }
        }
        onApplied={setApplied}
        secondaryAction={
          <RobotoButton variant="ghost" onClick={() => onClose(false)}>
            Cancel
          </RobotoButton>
        }
      />
    </div>
  );
}
